import { Controller } from '@hotwired/stimulus'
import ReactRailsUJS from 'react_ujs'
import { debounce } from 'underscore'
import { performWithCsrfRetry } from 'helpers/csrf'
import { disable, enable } from '../../../helpers/custom_events_helper'

export default class Autosave extends Controller {
  static values = {
    url: String,
    finalizeUrl: String,
    debounceTime: { type: Number, default: 900 },
    jitterProtectionTime: { type: Number, default: 3000 },
    responseKind: { type: String, default: 'json' }
  }

  connect() {
    this.jitterProtectedElements = new Array()
    this.fieldsPendingUpdate = new Set()
    this.scrollSnapshot = null
    this.scrollRestoreFrame = null
    this.scrollReleaseFrame = null

    // Create a debounced save function
    this.debouncedSave = debounce(this.saveChanges, this.debounceTimeValue)

    this.element.addEventListener('change', this.inputChanged)
    this.element.addEventListener('turbo:submit-start', this.onSubmitStart)
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd)

    document.addEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.addEventListener('turbo:before-morph-element', this.ignoreReactComponents)
    document.addEventListener('turbo:before-render', this.unmmountReactComponents)
    document.addEventListener('turbo:before-stream-render', this.preserveScrollPosition)
    window.addEventListener('pageshow', this.pageshowHandler)

    console.log('SuperForm: auto-save enabled.')
  }

  disconnect() {
    this.element.removeEventListener('change', this.inputChanged)
    this.element.removeEventListener('turbo:submit-start', this.onSubmitStart)
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd)

    document.removeEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.removeEventListener('turbo:before-morph-element', this.ignoreReactComponents)
    document.removeEventListener('turbo:before-render', this.unmmountReactComponents)
    document.removeEventListener('turbo:before-stream-render', this.preserveScrollPosition)
    window.removeEventListener('pageshow', this.pageshowHandler)

    if (this.scrollRestoreFrame) cancelAnimationFrame(this.scrollRestoreFrame)
    if (this.scrollReleaseFrame) cancelAnimationFrame(this.scrollReleaseFrame)
    this.releaseScrollSnapshot()
  }

  pageshowHandler = e => {
    if (e.persisted) Turbo.visit(location.href, { action: 'replace' })
  }

  inputChanged = event => {
    let target = event.target
    let elementName = target.name
    this.fieldsPendingUpdate.add(elementName)

    this.jitterProtectedElements.push(elementName)
    // Pop the oldest input name off the jitterProtectedElements array after 5x the debounce time.
    setTimeout(() => {
      this.jitterProtectedElements.shift()
    }, this.jitterProtectionTimeValue)

    // Remove error classes from elements because jitter protection will prevent them from updating.
    this.element.querySelectorAll(`[name="${elementName}"]`).forEach(el => el.classList.remove('is-invalid'))

    this.debouncedSave()
  }

  saveChanges = () => {
    // Copy the changed fields to a new array to prevent mutation during iteration.
    const updateBatch = Array.from(this.fieldsPendingUpdate)
    const updateBatchSize = updateBatch.length

    if (updateBatchSize === 0) {
      document.dispatchEvent(new CustomEvent('autosave:completed', { detail: { updateBatchSize: updateBatchSize } }))
      return
    }

    // Clear the changed fields tracking object
    this.fieldsPendingUpdate = new Set()

    const data = new FormData()

    // Process all tracked elements and add them to the form data
    updateBatch.forEach(name => {
      if (name.match(/\[\]\[/)) {
        // Handle BasicList fields
        const fieldset = this.element.querySelector(`[name="${name}"]`).closest('fieldset')
        const elements = fieldset.querySelectorAll('input, textarea')
        this.appendToFormData(elements, data)
      } else {
        const elements = this.collectFieldElements(name)
        this.appendToFormData(elements, data)
      }
    })

    document.dispatchEvent(new CustomEvent('autosave:started'))

    performWithCsrfRetry('patch', this.urlValue, {
      body: data,
      responseKind: this.responseKindValue
    })
      .then(({ request, response }) => {
        if (response.ok) {
          document.dispatchEvent(new CustomEvent('autosave:success', { detail: { response } }))
        } else if (response.statusCode === 401) {
          // If still unauthorized after Csrf retry, reload page to redirect to proper URL
          window.location.reload()
        } else {
          console.error('SuperForm: autosave failed', response)
          document.dispatchEvent(new CustomEvent('autosave:failed', { detail: { response } }))
        }
      })
      .catch(error => {
        console.error('SuperForm: autosave failed', error)
        document.dispatchEvent(new CustomEvent('autosave:failed', { detail: { error } }))
      })
      .finally(() => {
        document.dispatchEvent(new CustomEvent('autosave:completed', { detail: { updateBatchSize } }))
      })
  }

  appendToFormData = (elements, formData) => {
    Array.from(elements).forEach(el => {
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) {
          formData.append(el.name, el.value)
        }
      } else {
        formData.append(el.name, el.value)
      }
    })
  }

  // Collects form elements for a given field name.
  // For autosave-group fields, returns all elements in the group.
  // For regular fields, returns all elements with the given name.
  collectFieldElements = fieldName => {
    const changedElement = this.element.querySelector(`[name="${fieldName}"]`)

    if (changedElement?.dataset?.autosaveGroupField !== undefined) {
      const groupContainer = changedElement.closest('[data-autosave-group]')
      if (groupContainer) {
        return groupContainer.querySelectorAll('[data-autosave-group-field]')
      }
      return [changedElement]
    }

    return this.element.querySelectorAll(`[name="${fieldName}"]`)
  }

  // Prevents jitter when an input element is morphed by Turbo.
  preventInputElementJitter = event => {
    const detail = event.detail
    const newEl = detail.newElement
    if (!newEl?.name) return

    const target = event.target

    // prevent turbo-morph from replacing protected inputs, but ensure that new classes
    // are transfered from the new element

    function applyNewElementClasses() {
      const existing = document.querySelectorAll(`[name='${newEl.name}']`)
      existing.forEach(el => {
        el.className = newEl.className
      })
    }

    if (this.jitterProtectedElements.includes(newEl.name)) {
      event.preventDefault()
      applyNewElementClasses()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for recently updated element ${newEl.name}`)
    } else if (!target.readOnly && target.name === document.activeElement.name) {
      event.preventDefault()
      applyNewElementClasses()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for active element ${newEl.name}`)
    }
  }

  captureScrollSnapshot = () => {
    const scrollContainer = document.getElementById('sidebar-scroll-container')
    if (!scrollContainer) return null

    const formScrollContainer = scrollContainer.querySelector('.super-form-scrollable-container')
    const containers = [scrollContainer, formScrollContainer]
      .filter(Boolean)
      .map(element => ({
        element,
        overflowAnchor: element.style.overflowAnchor
      }))

    containers.forEach(({ element }) => {
      element.style.overflowAnchor = 'none'
    })

    return {
      scrollContainer,
      scrollTop: scrollContainer.scrollTop,
      formScrollContainer,
      formScrollTop: formScrollContainer?.scrollTop,
      containers
    }
  }

  restoreScrollSnapshot = () => {
    if (!this.scrollSnapshot) return

    const { scrollContainer, scrollTop, formScrollContainer, formScrollTop } = this.scrollSnapshot

    scrollContainer.scrollTop = scrollTop

    if (formScrollContainer && formScrollTop !== undefined) {
      formScrollContainer.scrollTop = formScrollTop
    }
  }

  releaseScrollSnapshot = () => {
    if (!this.scrollSnapshot) return

    this.scrollSnapshot.containers.forEach(({ element, overflowAnchor }) => {
      element.style.overflowAnchor = overflowAnchor
    })

    this.scrollSnapshot = null
  }

  scheduleScrollRestore = () => {
    if (!this.scrollSnapshot) return

    if (this.scrollRestoreFrame) cancelAnimationFrame(this.scrollRestoreFrame)
    if (this.scrollReleaseFrame) cancelAnimationFrame(this.scrollReleaseFrame)

    this.scrollRestoreFrame = requestAnimationFrame(() => {
      this.restoreScrollSnapshot()

      this.scrollReleaseFrame = requestAnimationFrame(() => {
        this.restoreScrollSnapshot()
        this.releaseScrollSnapshot()
        this.scrollRestoreFrame = null
        this.scrollReleaseFrame = null
      })
    })
  }

  // Preserve the form scroll position across Turbo Stream morphs. Turbo's
  // document-level scroll preservation does not help with this nested scroll
  // container, and browser scroll anchoring can still produce a visible nudge
  // during multi-stream autosave updates.
  preserveScrollPosition = event => {
    if (!this.scrollSnapshot) {
      this.scrollSnapshot = this.captureScrollSnapshot()
    }
    if (!this.scrollSnapshot) return

    const originalRender = event.detail.render

    event.detail.render = async (streamElement) => {
      await originalRender(streamElement)
      this.restoreScrollSnapshot()
      this.scheduleScrollRestore()
    }
  }

  ignoreReactComponents = event => {
    const target = event.target

    if (target.dataset['reactClass']) {
      event.preventDefault()
    }
  }

  finalize = async event => {
    // Prevent the default form submission to avoid race condition with autosave
    event?.preventDefault()

    if (this.debouncedSave?.cancel) this.debouncedSave.cancel()

    // Wait for autosave to complete and for the autosave:completed event
    // to ensure database transaction has committed
    const autosaveCompleted = new Promise(resolve => {
      const handler = () => {
        document.removeEventListener('autosave:completed', handler)
        resolve()
      }
      document.addEventListener('autosave:completed', handler)
    })

    await this.saveChanges()
    await autosaveCompleted

    this.element.action = this.finalizeUrlValue
    this.element.requestSubmit(event?.currentTarget)
  }

  // Disable ALL submit controls at the start of ANY submit (Finish or Save & Exit)
  onSubmitStart = e => {
    const form = e.target
    const submitters = form.querySelectorAll('button[type="submit"], input[type="submit"]')

    submitters.forEach(btn => {
      disable(btn)
      btn.disabled = true
    })
  }

  // Restore submit controls after the submit completes (success or 422)
  onSubmitEnd = e => {
    const form = e.target
    const submitters = form.querySelectorAll('button[type="submit"], input[type="submit"]')

    submitters.forEach(btn => {
      enable(btn)
      btn.disabled = false
    })
  }

  unmmountReactComponents = () => ReactRailsUJS.unmountComponents()
}

export { Autosave }
